import * as THREE from 'three';
import Application from '../Application';
import { CameraKey } from '../Camera/Camera';
import UIEventBus from '../UI/EventBus';
/**
 * Hitboxes: lightweight click / hover manager for 3D desk objects.
 *
 * The WebGL canvas has `pointer-events: none` (so the monitor iframe stays
 * usable), so we listen on `window` in the capture phase, raycast from the
 * camera and, when an interactive object is hit, stop the event before the
 * Camera's "click anywhere = toggle idle/desk" handler sees it.
 *
 * Add `?hitboxes` to the URL to render invisible hitboxes as red wireframes.
 */
export const DESK_OVERLAY_ID = 'desk-overlay';
export default class Hitboxes {
    constructor() {
        /**
         * The desk camera drifts with the mouse, so objects keep moving under a
         * still cursor. Re-pick a few times per second to keep hover state honest.
         */
        this.frame = 0;
        this.onMouseMove = (event) => {
            // Events forwarded from inside the monitor iframe
            // @ts-ignore
            if (event.inComputer) {
                this.setHovered(null);
                return;
            }
            this.lastMove = { x: event.clientX, y: event.clientY };
            if (this.moveScheduled)
                return;
            this.moveScheduled = true;
            window.requestAnimationFrame(() => {
                this.moveScheduled = false;
                if (!this.lastMove)
                    return;
                const { x, y } = this.lastMove;
                const entry = this.isInteractive() ? this.pick(x, y) : null;
                this.setHovered(entry);
                if (entry) {
                    UIEventBus.dispatch('deskHover', { label: entry.label, x, y });
                }
            });
        };
        this.onMouseDown = (event) => {
            const target = event.target;
            // Clicks inside the desk overlay UI must never reach the camera handlers
            if (target && target.closest && target.closest('#' + DESK_OVERLAY_ID)) {
                event.stopPropagation();
                return;
            }
            // @ts-ignore
            if (event.inComputer)
                return;
            if (target && target.id === 'prevent-click')
                return;
            if (!this.isInteractive())
                return;
            const entry = this.pick(event.clientX, event.clientY);
            if (!entry) {
                // Camera is about to move away: drop any stale tooltip
                this.setHovered(null);
                UIEventBus.dispatch('deskHover', null);
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            this.setHovered(null);
            // Clicked a bezel prop from the monitor close-up: drop the monitor
            // state quietly so the next mouse move doesn't send the camera back
            // to the desk while it zooms onto the prop.
            if (this.isMonitorView()) {
                const world = this.application.world;
                if (world && world.monitorScreen)
                    world.monitorScreen.releaseMonitor();
            }
            entry.onClick();
        };
        this.application = new Application();
        this.scene = this.application.scene;
        this.camera = this.application.camera;
        this.sizes = this.application.sizes;
        this.raycaster = new THREE.Raycaster();
        this.pointer = new THREE.Vector2();
        this.entries = [];
        this.hovered = null;
        this.overlayOpen = false;
        this.lastMove = null;
        this.moveScheduled = false;
        this.debug = new URLSearchParams(window.location.search).has('hitboxes');
        UIEventBus.on('deskOverlayOpen', () => {
            this.overlayOpen = true;
            this.setHovered(null);
        });
        UIEventBus.on('deskOverlayClosed', () => {
            this.overlayOpen = false;
        });
        window.addEventListener('mousemove', this.onMouseMove, true);
        window.addEventListener('mousedown', this.onMouseDown, true);
    }
    /**
     * Register one or more objects (meshes or groups) as a single clickable item.
     */
    register(id, objects, options) {
        this.entries.push(Object.assign({ id, objects: Array.isArray(objects) ? objects : [objects] }, options));
    }
    /**
     * Create an invisible box (still raycastable) and add it to the scene.
     */
    createBox(name, position, size) {
        const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            wireframe: this.debug,
            transparent: true,
            opacity: this.debug ? 0.6 : 0,
            depthWrite: false,
            colorWrite: this.debug,
        });
        const box = new THREE.Mesh(new THREE.BoxGeometry(size.x, size.y, size.z), material);
        box.name = name;
        box.position.copy(position);
        box.userData.isHitbox = true;
        this.scene.add(box);
        return box;
    }
    /**
     * Desk props are clickable at the desk, in the monitor close-up (the
     * bezel stickies sit right next to the screen) and in free cam.
     */
    isInteractive() {
        if (this.overlayOpen)
            return false;
        if (this.camera.freeCam)
            return true;
        return (this.camera.currentKeyframe === CameraKey.DESK ||
            this.isMonitorView());
    }
    isMonitorView() {
        return (this.camera.currentKeyframe === CameraKey.MONITOR ||
            this.camera.targetKeyframe === CameraKey.MONITOR);
    }
    findEntry(object) {
        let current = object;
        while (current) {
            for (const entry of this.entries) {
                if (entry.objects.indexOf(current) !== -1)
                    return entry;
            }
            current = current.parent;
        }
        return null;
    }
    /** Opaque (or screen-occluding) meshes block clicks to things behind them. */
    isOccluder(object) {
        const mesh = object;
        if (!mesh.isMesh || mesh.userData.isHitbox)
            return false;
        const material = mesh.material;
        if (!material)
            return false;
        return (!material.transparent || material.blending === THREE.NoBlending);
    }
    pick(clientX, clientY) {
        if (!this.entries.length)
            return null;
        this.pointer.set((clientX / this.sizes.width) * 2 - 1, -(clientY / this.sizes.height) * 2 + 1);
        this.raycaster.setFromCamera(this.pointer, this.camera.instance);
        const hits = this.raycaster.intersectObjects(this.scene.children, true);
        for (const hit of hits) {
            const entry = this.findEntry(hit.object);
            if (entry)
                return entry;
            if (this.isOccluder(hit.object))
                return null;
        }
        return null;
    }
    setHovered(entry) {
        if (entry === this.hovered)
            return;
        if (this.hovered && this.hovered.onHoverChange) {
            this.hovered.onHoverChange(false);
        }
        if (entry && entry.onHoverChange)
            entry.onHoverChange(true);
        this.hovered = entry;
        if (!entry)
            UIEventBus.dispatch('deskHover', null);
    }
    update() {
        this.frame++;
        if (this.frame % 6 !== 0 || !this.lastMove || this.moveScheduled)
            return;
        const { x, y } = this.lastMove;
        const entry = this.isInteractive() ? this.pick(x, y) : null;
        this.setHovered(entry);
        if (entry) {
            UIEventBus.dispatch('deskHover', { label: entry.label, x, y });
        }
    }
}
